import { Click, PointerInfoClick } from "../action/Click.js";
import { Caps } from "../core/Caps.js";
import { Signal } from "../core/Signal.js";
import { Matrix2d } from "../geom/Matrix2d.js";
import { IPoint } from "../geom/Point.js";
import { Point3d } from "../geom/Point3d.js";
import { Transform } from "../geom/Transform.js";
import { ITransformMode } from "../geom/transformmode/ITransformMode.js";
import { TransformMode } from "../geom/transformmode/TransformMode.js";
import { Calc } from "../math/Calc.js";
import { object } from "../util/ObjectUtil.js";
import { Resize, ResizeSignal } from "./Resize.js";
import { IScrollViewLimit } from "./scrollviewlimit/IScrollViewLimit.js";
import { ScrollViewLimit } from "./scrollviewlimit/ScrollViewLimit.js";
import { IScrollViewResize } from "./scrollviewresize/IScrollViewResize.js";
import { ScrollViewResize } from "./scrollviewresize/ScrollViewResize.js";
import { IScrollViewScale } from "./scrollviewscale/IScrollViewScale.js";
import { ScrollViewScale } from "./scrollviewscale/ScrollViewScale.js";
import { IScrollViewWheel } from "./scrollviewwheel/IScrollViewWheel.js";
import { ScrollViewWheel } from "./scrollviewwheel/ScrollViewWheel.js";

export interface ScrollViewContent {
    element: HTMLElement;
    width: number;
    height: number;
}

export interface ScrollViewTransform {
    scale: number;
    x: number;
    y: number;
}

export type ScrollViewScaleLimit = IScrollViewScale | number;

export interface ScrollViewOptions {
    doubleClickZoom?: boolean;
}

export class ScrollView {
    public static DEFAULT_TRANSFORM_MODE: ITransformMode = TransformMode.Rectangle;

    public onTransform = Signal.create<[ScrollViewTransform]>();
    public onScale = Signal.create<[ScrollViewTransform]>();
    public onTranslate = Signal.create<[ScrollViewTransform]>();
    public onResize = Signal.create<[ScrollView, number, number]>();
    public onClick = Signal.create<[ScrollView, PointerInfoClick]>();

    public element: HTMLDivElement;
    public elasticityX: number = 0;
    public elasticityY: number = 0;
    public elasticityScale: number = 0;
    public flingX: number = 200;
    public flingY: number = 200;
    public wheel: IScrollViewWheel = ScrollViewWheel.Zoom;
    public resizeBehaviour: IScrollViewResize = ScrollViewResize.Center;
    public limitX: IScrollViewLimit = ScrollViewLimit.Auto;
    public limitY: IScrollViewLimit = ScrollViewLimit.Auto;
    public minScale: ScrollViewScaleLimit = ScrollViewScale.Contain;
    public maxScale: ScrollViewScaleLimit = ScrollViewScale.Original;
    public tranformMode: ITransformMode = TransformMode.Rectangle;
    public pinch: boolean = true;
    public pan: boolean = true;

    private _viewContent: ScrollViewContent = null;
    private _container: HTMLDivElement;
    private _content: HTMLDivElement;
    private _matrix: Matrix2d = new Matrix2d();
    private _scaleTarget: number = NaN;
    private _xTarget: number = NaN;
    private _yTarget: number = NaN;
    private _lastWidth: number = 0;
    private _lastHeight: number = 0;
    private _lastX: number = 0;
    private _lastY: number = 0;
    private _lastScale: number = 1;
    private _tween: gsap.core.Tween = null;
    private _clampOffsetX = 0;
    private _clampOffsetY = 0;
    private _clampOffsetScale = 0;
    private _pivotLocal: IPoint = null;
    private _distance: number = 1;
    private _distanceScale: number = 1;
    private _actor: Click;
    private _wheelListener: (e: WheelEvent) => void;
    private _resizeListener: () => void;
    private _lastPointerUpTime: number = 0;
    private _onResize: ResizeSignal;

    constructor(options?: ScrollViewOptions) {
        options = object.merge({
            doubleClickZoom: true
        }, options || {});

        this.tranformMode = ScrollView.DEFAULT_TRANSFORM_MODE;

        this.element = document.createElement("div");
        this.element.className = "e5-scroll-view";
        
        this.element.innerHTML = `<div class="e5-scroll-view-container"><div class="e5-scroll-view-content"></div></div>`;

        // var sb = new ScrollbarScrollView(this);
        // this.element.appendChild(sb.element);
        // var sb = new ScrollbarScrollView(this, true);
        // this.element.appendChild(sb.element);

        this._container = this.element.querySelector(".e5-scroll-view-container");
        this._container.tabIndex = 0;
        this._content = this.element.querySelector(".e5-scroll-view-content");

        this._actor = new Click(this._container, {
            doubleClick: options.doubleClickZoom,
            preventDefault: true
        });

        this._wheelListener = (e) => this.handleMouseWheel(e);
        this._resizeListener = () => this.resize();

        this._actor.onDoubleClick.add(this.handleDoubleClick, this);
        this._actor.onDown.add(this.handleDown, this);
        this._actor.onMove.add(this.handleMove, this);
        this._actor.onUp.add(this.handleUp, this);
        this._actor.onClick.add((info: PointerInfoClick) => {
            // if (info.isClick) {
            this.onClick.dispatch(this, info);
            // }
        });
        this._container.addEventListener("wheel", this._wheelListener, Caps.passiveEventLister ? { passive: false } : false);
        // window.addEventListener("resize", this._resizeListener);


        this._onResize = Resize.create(this.element);
        this._onResize.add(this._resizeListener);
    }

    private handleDoubleClick(pointer: PointerInfoClick): void {
        var min = this.getMinScale();
        var max = this.getMaxScale();
        var minDiff = this.scale - min;
        var maxDiff = max - this.scale;
        var scale = minDiff < maxDiff ? max : min;
        this.scaleAtGlobal(scale, pointer.x, pointer.y, 0.3);
    }

    public cancel(): void {
        this.killTween();
        this._actor.cancel();
    }

    private handleDown(pointers: PointerInfoClick[]): void {
        this._content.classList.add("dragging");
        this.killTween();

        this.updatePivot(false);

        if (this._actor.pointers.length > 1) {
            this._distance = this._actor.distance;
        }
    }

    private handleMove(pointers: PointerInfoClick[]): void {
        var center = this._actor.center;

        var local = this.screenToContent(center.x, center.y);

        var deltaX = 0;
        var deltaY = 0;

        //Translate (Absolute)
        if (this.pan) {
            deltaX = local.x - this._pivotLocal.x;
            deltaY = local.y - this._pivotLocal.y;
            this._matrix.appendPosition(deltaX, deltaY);
        }

        //Scale (Absolute)
        if (this.pinch && this._actor.pointers.length > 1) {
            var scaleRaw = this._distanceScale * (this._actor.distance / this._distance);
            var scale = this.clampScale(scaleRaw, this.elasticityScale);
            var t = this.scaleAtCalc(scale, local.x + deltaX, local.y + deltaY, false);
            this._matrix.m11 = t.scale;
            this._matrix.m22 = t.scale;
            this._matrix.m31 = t.x;
            this._matrix.m32 = t.y;
            this._clampOffsetScale = scaleRaw - scale;
        }

        //Clamp translate
        var x = this.clampX(this._matrix.m31, this._matrix.m11, this.elasticityX);
        var y = this.clampY(this._matrix.m32, this._matrix.m11, this.elasticityY);
        this._clampOffsetX = this._matrix.m31 - x;
        this._clampOffsetY = this._matrix.m32 - y;
        this._matrix.m31 = x;
        this._matrix.m32 = y;

        //Apply
        this.updateTransform();

        //Update pivot: otherwise it will stick to pivot-point 
        //even if the x/y position is clamped.
        if (this.pan && (!this.elasticityX || !this.elasticityY)) {
            var p = this.screenToContent(center.x, center.y);
            if (!this.elasticityX)
                this._pivotLocal.x = p.x;
            if (!this.elasticityY)
                this._pivotLocal.y = p.y;
        }

        if (this.pinch && !this.elasticityScale) {
            this._distanceScale = this.scale;
            this._distance = this._actor.distance;
        }
    }

    private handleUp(pointers: PointerInfoClick[]): void {
        this.updatePivot(true);

        if (this._actor.pointers.length > 1) {
            this._distance = this._actor.distance;
        }

        if (this._actor.pointers.length === 0) {
            this._clampOffsetX = 0;
            this._clampOffsetY = 0;
            this._clampOffsetScale = 0;
            this._content.classList.remove("dragging");
            var pointer = pointers[0];
            if (!pointer.isDoubleClick) {
                var scale = this.clampScale(this.scale);
                var centerLocal = this.containerToContent(this.width * 0.5, this.height * 0.5);
                var t = this.scaleAtCalc(scale, centerLocal.x, centerLocal.y);
                // if ((pointers[0].changeTime - this._lastPointerUpTime) > 300) {
                // if (!pointers[0].multipointer) {

                var fadeTime = 0.3;
                if (this.pan && (this.flingX > 0 || this.flingY > 0)) {
                    var magnitude = Math.sqrt(pointer.velocityX * pointer.velocityX + pointer.velocityY * pointer.velocityY);
                    if (magnitude > 0.1) {
                        var delta = Transform.toLocalDelta(this._container, pointer.velocityX, pointer.velocityY, 0, new Point3d(), this.tranformMode);
                        if (this.flingX > 0)
                            t.x = this.clampX(t.x + delta.x * this.flingX, scale);
                        if (this.flingY > 0)
                            t.y = this.clampY(t.y + delta.y * this.flingY, scale);
                        fadeTime = 0.5;
                    }
                }
                this.setTransform(t, fadeTime);
            }
        }

        this._lastPointerUpTime = (new Date()).getTime();
    }

    private updatePivot(useOffset: boolean): void {
        if (this._actor.pointers.length > 0) {
            var center = this._actor.center;

            if (this._actor.pointers.length > 1) {
                this._distanceScale = this.scale;
                this._distance = this._actor.distance;
            }

            if (useOffset || this._actor.pointers.length > 1) {
                if (this.elasticityX > 0 || this.elasticityY > 0) {
                    var delta = Transform.toGlobalDelta(this.element, this._clampOffsetX, this._clampOffsetY, 0, new Point3d(), this.tranformMode);
                    if (this.elasticityX > 0)
                        center.x -= delta.x;
                    if (this.elasticityY > 0)
                        center.y -= delta.y;
                }
                if (this.elasticityScale > 0) {
                    this._distanceScale += this._clampOffsetScale;
                }
            }
            this._pivotLocal = this.screenToContent(center.x, center.y);
        }
    }

    private handleMouseWheel(e: WheelEvent): void {
        try {
            e.preventDefault();
        } catch (error) {

        }

        if (this._actor.pointers.length > 0)
            return;
        var delta = e.deltaY < 0 ? -1 : 1;
        this.wheel.apply(this, delta, e);
    }

    public scaleAtGlobal(scale: number, clientX: number, clientY: number, time: number = 0): void {
        var local = this.screenToContent(clientX, clientY);
        this.scaleAt(scale, local.x, local.y, time);
    }

    public scaleAt(scale: number, localX: number, localY: number, time: number = 0): void {
        this.setTransform(this.scaleAtCalc(scale, localX, localY), time);
    }

    public scaleAtCenter(scale: number, relativeX: number = 0.5, relativeY: number = 0.5, time: number = 0): void {
        this.setTransform(this.scaleAtCalc(scale, this.contentWidth * relativeX, this.contentHeight * relativeY), time);
    }

    private scaleAtCalc(scale: number, localX: number, localY: number, clamp: boolean = true): ScrollViewTransform {
        if (clamp)
            scale = this.clampScale(scale);
        var scaleDelta = scale / this.scale
        var matrix = this._matrix.clone();
        matrix.appendPosition(localX, localY);
        matrix.appendScale(scaleDelta, scaleDelta);
        matrix.appendPosition(-localX, -localY);
        return {
            scale: scale,
            x: clamp ? this.clampX(matrix.m31, scale) : matrix.m31,
            y: clamp ? this.clampY(matrix.m32, scale) : matrix.m32
        };
    }

    public lookAt(localX: number, localY: number, scale: number = this.scale, time: number = 0): void {
        scale = this.clampScale(scale);
        this.setTransform({
            scale: scale,
            x: this.clampX(this.width * 0.5 - localX * scale, scale),
            y: this.clampY(this.height * 0.5 - localY * scale, scale)
        }, time)
    }

    private killTween(): void {
        if (this._tween) {
            this._tween.kill();
            this._tween = null;
        }
    }

    public getTransform(): ScrollViewTransform {
        return {
            scale: this.scale,
            x: this.x,
            y: this.y
        }
    }

    public getTransformTarget(): ScrollViewTransform {
        return {
            scale: isNaN(this._scaleTarget) ? this._matrix.m11 : this._scaleTarget,
            x: isNaN(this._xTarget) ? this._matrix.m31 : this._xTarget,
            y: isNaN(this._yTarget) ? this._matrix.m32 : this._yTarget
        };
    }

    public setTransformClamped(transform: ScrollViewTransform, time: number = 0): void {
        this.setTransform(this.clampTransform(transform), time);
    }

    private clampTransform(transform: ScrollViewTransform): ScrollViewTransform {
        transform.scale = this.clampScale(transform.scale);
        transform.x = this.clampX(transform.x, transform.scale);
        transform.y = this.clampY(transform.y, transform.scale);
        return transform;
    }

    public setTransform(transform: ScrollViewTransform, time: number = 0): void {
        this.killTween();
        if (time <= 0) {
            this._matrix.m11 = transform.scale;
            this._matrix.m22 = transform.scale;
            this._matrix.m31 = transform.x;
            this._matrix.m32 = transform.y;
            this.updateTransform();
        } else {
            this._xTarget = transform.x;
            this._yTarget = transform.y;
            this._scaleTarget = transform.scale;
            this._tween = gsap.to(this._matrix, {
                duration: time,
                m11: transform.scale,
                m22: transform.scale,
                m31: transform.x,
                m32: transform.y,
                onUpdate: () => {
                    this.updateTransform();
                },
                onComplete: () => {
                    this._tween = null;
                    this._matrix.m11 = transform.scale;
                    this._matrix.m22 = transform.scale;
                    this._xTarget = NaN;
                    this._yTarget = NaN;
                    this._scaleTarget = NaN;
                }
            });
        }
    }

    public resize(): void {
        if (this._lastWidth === 0) {
            this._lastWidth = this.width;
            this._lastHeight = this.height;
        }
        var lw = this._lastWidth;
        var lh = this._lastHeight;
        var t: ScrollViewTransform = this.resizeBehaviour.getTransform(this, lw, lh);
        this._lastWidth = this.width;
        this._lastHeight = this.height;

        if (t) {
            this.setTransformClamped(t);
        } else {
            this.clamp();
            this.updateTransform();
        }

        this.onResize.dispatch(this, lw, lh);
    }

    public get x(): number {
        return this._matrix.m31;
    }

    public set x(x: number) {
        x = this.clampX(x);
        if (this._matrix.m31 === x)
            return;
        this._matrix.m31 = x;
        this.updateTransform();
    }

    public get xTarget(): number {
        return isNaN(this._xTarget) ? this.x : this._xTarget;
    }

    public get y(): number {
        return this._matrix.m32;
    }

    public set y(y: number) {
        y = this.clampY(y);
        if (this._matrix.m32 === y)
            return;
        this._matrix.m32 = y;
        this.updateTransform();
    }

    public get yTarget(): number {
        return isNaN(this._yTarget) ? this.y : this._yTarget;
    }

    public get scale(): number {
        return this._matrix.m11;
    }

    public set scale(scale: number) {
        scale = this.clampScale(scale);
        if (this._matrix.m11 === scale)
            return;
        this._matrix.m11 = scale;
        this._matrix.m22 = scale;
        this.updateTransform();
    }

    public get scaleTarget(): number {
        return isNaN(this._scaleTarget) ? this.scale : this._scaleTarget;
    }

    public applyScale(limit: ScrollViewScaleLimit, time: number = 0): void {
        var scale = this.parseScaleLimit(limit);
        var t: ScrollViewTransform = {
            scale: this.clampScale(scale),
            x: this.clampX(this.x, scale),
            y: this.clampY(this.y, scale)
        };
        this.setTransform(t, time);
    }

    public applyLimit(limit: IScrollViewLimit, time: number = 0): void {
        var t: ScrollViewTransform = {
            scale: this.scale,
            x: this.clampX(Calc.clamp(this.x, limit.getMinX(this, this.scale), limit.getMaxX(this, this.scale))),
            y: this.clampY(Calc.clamp(this.y, limit.getMinY(this, this.scale), limit.getMaxY(this, this.scale)))
        };
        this.setTransform(t, time);
    }

    public screenToContent(clientX: number, clientY: number): IPoint {
        return Transform.toLocal(this._content, clientX, clientY, 0, new Point3d(), this.tranformMode);
    }

    public contentToScreen(localX: number, localY: number): IPoint {
        return Transform.toGlobal(this._content, localX, localY, 0, new Point3d(), this.tranformMode);
    }

    public containerToContent(containerX: number, containerY: number): IPoint {
        var localX = (containerX - this.x) / this.scale;
        var localY = (containerY - this.y) / this.scale;
        return { x: localX, y: localY };
    }

    public contentToContainer(localX: number, localY: number): IPoint {
        var containerX = localX * this.scale + this.x;
        var containerY = localY * this.scale + this.y;
        return { x: containerX, y: containerY };
    }

    private updateTransform(): void {
        this._content.style.transform = this._matrix.getCss();

        var transform = this.getTransform();
        this.onTransform.dispatch(transform);

        var scale = this.scale;
        if (scale != this._lastScale) {
            this._lastScale = scale;
            this.onScale.dispatch(transform);
        }
        var x = this.x;
        var y = this.y;
        if (x != this._lastX || y != this._lastY) {
            this._lastX = x;
            this._lastY = y;
            this.onTranslate.dispatch(transform);
        }
    }

    public hasOverflow(targetTransform: boolean = false): boolean {
        return this.hasOverflowHorizontal(targetTransform) || this.hasOverflowVertical(targetTransform);
    }

    public hasOverflowVertical(targetTransform: boolean = false): boolean {
        var rh = Math.floor(this.contentHeight * (targetTransform ? this.scaleTarget : this.scale)) - 0.01;
        if (rh > this.height) {
            return true;
        }
        return false;
    }

    public hasOverflowHorizontal(targetTransform: boolean = false): boolean {
        var rw = Math.floor(this.contentWidth * (targetTransform ? this.scaleTarget : this.scale)) - 0.01;
        if (rw > this.width) {
            return true;
        }
        return false;
    }

    private clamp(): void {
        this._matrix.m11 = this.clampScale(this.scale);
        this._matrix.m22 = this._matrix.m11;
        this._matrix.m31 = this.clampX(this._matrix.m31, this._matrix.m11);
        this._matrix.m32 = this.clampY(this._matrix.m32, this._matrix.m11);
    }

    private clampScale(scale: number, elasticity: number = 0): number {
        if (elasticity >= 1)
            return scale;
        if (elasticity > 0)
            return Calc.clampFade(scale, this.getMinScale(), this.getMaxScale(), elasticity);
        return Calc.clamp(scale, this.getMinScale(), this.getMaxScale());
    }

    public getMaxScale(): number {
        return Math.max(this.parseScaleLimit(this.minScale), this.parseScaleLimit(this.maxScale));
    }

    public getMinScale(): number {
        return Math.min(this.parseScaleLimit(this.minScale), this.parseScaleLimit(this.maxScale));
    }

    private parseScaleLimit(limit: ScrollViewScaleLimit): number {
        return (typeof limit === "number") ? limit : limit.getScale(this);
    }

    private clampX(x: number, scale: number = this.scale, elasticity: number = 0): number {
        if (elasticity >= 1)
            return x;
        if (elasticity > 0)
            return Calc.clampFade(x, this.limitX.getMinX(this, scale), this.limitX.getMaxX(this, scale), elasticity);
        return Calc.clamp(x, this.limitX.getMinX(this, scale), this.limitX.getMaxX(this, scale));
    }

    private clampY(y: number, scale: number = this.scale, elasticity: number = 0): number {
        if (elasticity >= 1)
            return y;
        if (elasticity > 0)
            return Calc.clampFade(y, this.limitY.getMinY(this, scale), this.limitY.getMaxY(this, scale), elasticity);
        return Calc.clamp(y, this.limitY.getMinY(this, scale), this.limitY.getMaxY(this, scale));
    }

    public get content(): ScrollViewContent {
        return this._viewContent;
    }

    public set content(content: ScrollViewContent) {
        if (this._viewContent === content)
            return;
        this.killTween();
        if (this._viewContent) {
            this._viewContent.element.remove();
        }
        this._viewContent = content;
        if (this._viewContent) {
            this._content.style.width = this._viewContent.width + "px";
            this._content.style.height = this._viewContent.height + "px";
            this._content.appendChild(this._viewContent.element);
            this._matrix.m11 = this.getMinScale();
            this._matrix.m22 = this._matrix.m11;
            this.clamp();
            this.updateTransform();
        }
    }

    public get width(): number {
        return this.element.offsetWidth || 10;
    }

    public get height(): number {
        return this.element.offsetHeight || 10;
    }

    public get contentWidth(): number {
        if (!this._viewContent)
            return 0;
        return this._viewContent.width || this._viewContent.width;
    }

    public get contentHeight(): number {
        if (!this._viewContent)
            return 0;
        return this._viewContent.height || this._viewContent.height;
    }

    public dispose(): void {
        this.killTween();
        this.onScale.dispose();
        this.onScale = null;
        this.onTransform.dispose();
        this.onTransform = null;
        this.onTranslate.dispose();
        this.onTranslate = null;
        this._container.removeEventListener("wheel", this._resizeListener);
        this._container = null;
        // window.removeEventListener("resize", this._resizeListener);
        this._onResize.dispose();
        this._actor.dispose();
        this._onResize = null;
        this._actor = null;
        this._container = null;
        this._content = null;
        this._viewContent = null;
        this._matrix = null;
        this.element.remove();
    }
}